import { useState } from "react";
import { Button, Form, Spinner, Stack } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../../components/modal";
import { ServiceManager } from "../../../../services/ServiceManager";
import { MisType } from "../../../../app/enums";
import { SOMETHING_WENT_WRONG } from "../../../../app/constants";
import IRequeueRequest from "../../../../models/Requeue/RequeueRequest";
import IExtractLastSummary from "../../../../models/Requeue/ExtractLastSummary";
import IProcessLastSummary from "../../../../models/Requeue/ProcessLastSummary";

export type RequeueOperation = "extract" | "transform";

export interface IRequeueModalProps {
    establishmentID: string;
    operation: RequeueOperation;
    show: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export default function RequeueModal(props: IRequeueModalProps) {
    const { establishmentID, operation, show, onClose, onSuccess } = props;

    const isExtract = operation === "extract";
    const title = isExtract ? "Requeue Last Extract" : "Requeue Last Transform";

    const [forceRefresh, setForceRefresh] = useState(!isExtract);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data, isLoading, isError } = useQuery<IExtractLastSummary | IProcessLastSummary | null>({
        queryKey: ["requeueLast", operation, establishmentID],
        queryFn: async () => {
            const response = isExtract
                ? await ServiceManager.ExtractService.GetLastRequeue(establishmentID)
                : await ServiceManager.TransformService.GetLastRequeue(establishmentID);
            if (axios.isAxiosError(response)) {
                if (response.response?.status === 404) {
                    return null;
                }

                throw response;
            }
            return response;
        },
        enabled: show && !!establishmentID,
        refetchOnWindowFocus: false,
    });

    const extractSummary = isExtract ? (data as IExtractLastSummary) : undefined;
    const processSummary = !isExtract ? (data as IProcessLastSummary) : undefined;

    const hasPreviousRun = !!data;
    const isInFlight = data && (data as IExtractLastSummary | IProcessLastSummary).isInFlight;
    const canSubmit = hasPreviousRun && !isInFlight && !isSubmitting;

    function formatDate(value: string | null | undefined): string {
        if (!value) {
            return "Not processed";
        }
        return format(new Date(value), "dd/MM/yyyy HH:mm");
    }

    function getMisTypeLabel(misType: MisType): string {
        switch (misType) {
            case MisType.Sims:
                return "SIMS";
            case MisType.Arbor:
                return "Arbor";
            case MisType.Bromcom:
                return "Bromcom";
            default:
                return "Unknown";
        }
    }

    async function handleSubmit() {
        if (!canSubmit) {
            return;
        }

        setIsSubmitting(true);
        const request: IRequeueRequest = { forceRefresh: forceRefresh };

        try {
            const response = isExtract
                ? await ServiceManager.ExtractService.Requeue(establishmentID, request)
                : await ServiceManager.TransformService.Requeue(establishmentID, request);

            if (response && response.response) {
                if (response.response.status === 404) {
                    ServiceManager.ToastService.showError(response.response.data ?? "No previous run found.");
                } else if (response.response.status === 409) {
                    ServiceManager.ToastService.showError(response.response.data ?? "The previous run is currently in flight.");
                } else {
                    ServiceManager.ToastService.showError(response.response.data ?? SOMETHING_WENT_WRONG);
                }
            } else {
                ServiceManager.ToastService.showSuccess(`The last ${operation} has been requeued successfully.`);
                onSuccess();
                onClose();
            }
        } catch {
            ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleClose() {
        setForceRefresh(!isExtract);
        onClose();
    }

    return (
        <CustomModal
            isShow={show}
            handleClose={handleClose}
            size="md"
            header={
                <Stack direction="horizontal" gap={2}>
                    <FontAwesomeIcon icon={faRefresh} />
                    <span>{title}</span>
                </Stack>
            }
        >
            <Stack gap={3}>
                {isLoading && (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                )}

                {isError && (
                    <p className="text-danger">{SOMETHING_WENT_WRONG}</p>
                )}

                {!isLoading && !hasPreviousRun && (
                    <p>There is no previous {operation} run to requeue for this establishment.</p>
                )}

                {extractSummary && (
                    <Stack gap={2}>
                        <p>
                            <strong>MIS Type:</strong> {getMisTypeLabel(extractSummary.misType)} ({extractSummary.misTypeName})
                        </p>
                        <p>
                            <strong>Files:</strong> {extractSummary.fileCount}
                        </p>
                        <p>
                            <strong>Created:</strong> {formatDate(extractSummary.dateCreated)}
                        </p>
                        <p>
                            <strong>Processed:</strong> {formatDate(extractSummary.dateProcessed)}
                        </p>
                        {extractSummary.requeuedByUserName && (
                            <p className="text-muted">
                                Previously requeued by {extractSummary.requeuedByUserName}
                            </p>
                        )}
                    </Stack>
                )}

                {processSummary && (
                    <Stack gap={2}>
                        <p>
                            <strong>Created:</strong> {formatDate(processSummary.dateCreated)}
                        </p>
                        <p>
                            <strong>Processed:</strong> {formatDate(processSummary.dateProcessed)}
                        </p>
                        <p>
                            <strong>New people:</strong> {processSummary.newPeopleCount ?? 0} |{" "}
                            <strong>Modified:</strong> {processSummary.modifiedPeopleCount ?? 0} |{" "}
                            <strong>Missing:</strong> {processSummary.missingPeopleCount ?? 0} |{" "}
                            <strong>Leavers:</strong> {processSummary.leaversCount ?? 0} |{" "}
                            <strong>Photo changes:</strong> {processSummary.photosChangedCount ?? 0}
                        </p>
                        {processSummary.requeuedByUserName && (
                            <p className="text-muted">
                                Previously requeued by {processSummary.requeuedByUserName}
                            </p>
                        )}
                    </Stack>
                )}

                {isInFlight && (
                    <p className="text-warning">
                        The previous {operation} is currently in flight and cannot be requeued yet.
                    </p>
                )}

                <Form>
                    <Form.Check
                        type="checkbox"
                        id={`requeue-force-refresh-${operation}`}
                        label={`Force refresh all people after this ${operation} completes`}
                        checked={forceRefresh}
                        onChange={(e) => setForceRefresh(e.target.checked)}
                    />
                </Form>

                <Stack direction="horizontal" gap={2} className="justify-content-end mt-3">
                    <Button variant="outline-secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
                        {isSubmitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            "Confirm Requeue"
                        )}
                    </Button>
                </Stack>
            </Stack>
        </CustomModal>
    );
}
